import { ORIGIN } from 'src/constants';
import {
  COMMON_TRACKING_PARAMS,
  INTERNAL_TRACKING_PARAMS,
} from 'constants/search/allowedSearchParams';

const TRACKING_PARAMS = new Set<string>([
  ...COMMON_TRACKING_PARAMS,
  ...INTERNAL_TRACKING_PARAMS,
]);

/**
 * Build the canonical URL for a page: an absolute, self-referencing URL with
 * the hash and known tracking params (utm_*, gclid, ppc, ...) removed.
 *
 * Everything else in the query string is kept as-is: on pages like /search or
 * /blog?search=, the query IS the content (filters, search term, sort, page),
 * so stripping it would merge pages that show genuinely different results.
 * Only params that never affect content are removed, via the same
 * tracking-param list used to sanitize incoming search URLs.
 *
 * Already-absolute input (incl. protocol-relative `//host/…`) is returned as-is
 * so ORIGIN isn't double-prefixed; a relative path (`/blog`) gets ORIGIN
 * prepended.
 *
 * `origin` is injectable so the helper can be unit-tested without env setup.
 */
export const toCanonicalUrl = (
  url?: string,
  origin: string = ORIGIN ?? ''
): string | undefined => {
  if (!url) return undefined;

  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);

  const qIndex = withoutHash.indexOf('?');
  const path = qIndex === -1 ? withoutHash : withoutHash.slice(0, qIndex);
  const query = qIndex === -1 ? '' : withoutHash.slice(qIndex + 1);

  const cleanQuery = query
    .split('&')
    .filter(
      (segment) => segment && !TRACKING_PARAMS.has(segment.split('=')[0])
    )
    .join('&');

  const cleanPath = cleanQuery ? `${path}?${cleanQuery}` : path;

  if (/^(https?:)?\/\//i.test(cleanPath)) return cleanPath;

  return `${origin}${cleanPath}`;
};
